import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import {
    OdooCompany,
    SupplierFragmentFragment,
    useCompleteSupplierOdooAssociationMutation,
    TotadminPermission,
} from 'data/__generated__';
import { OdooFields } from './OdooSection';

type AssociationFields = { odooCompany: OdooCompany | null; odooSupplierId: number | null };

// This onboarding form owns its state and mutation; the supplier form only mounts after success.
export const SupplierOdooOnboarding = ({
    supplier,
    onCompleted,
}: {
    supplier: SupplierFragmentFragment;
    onCompleted: (supplier: SupplierFragmentFragment) => void;
}) => {
    const methods = useForm<AssociationFields>({
        defaultValues: {
            odooCompany: supplier.odooCompany ?? null,
            odooSupplierId: null,
        },
    });
    const [completeAssociation, { loading }] = useCompleteSupplierOdooAssociationMutation();
    const onSubmit = methods.handleSubmit(async ({ odooCompany, odooSupplierId }) => {
        if (!odooCompany || !odooSupplierId || loading) return;
        try {
            const { data } = await completeAssociation({
                variables: { supplierId: supplier._id, odooCompany, odooSupplierId },
            });
            const result = data?.completeSupplierOdooAssociation;
            if (result?.updatedSupplier) {
                onCompleted(result.updatedSupplier);
            } else {
                toast.error(result?.error || 'Impossible d’enregistrer l’association Odoo.');
            }
        } catch {
            toast.error('Impossible d’enregistrer l’association Odoo. Veuillez réessayer.');
        }
    });
    return (
        <Dialog open>
            <DialogContent
                showCloseButton={false}
                onEscapeKeyDown={(event) => event.preventDefault()}
                onInteractOutside={(event) => event.preventDefault()}
                className="sm:max-w-3xl border border-destructive"
            >
                <DialogHeader>
                    <DialogTitle>Associer {supplier.name} à Odoo</DialogTitle>
                    <DialogDescription>
                        Renseignez le partenaire Odoo et l’entité juridique pour accéder à la fiche fournisseur. Ces
                        valeurs ne seront plus modifiables après enregistrement.
                    </DialogDescription>
                </DialogHeader>
                <FormProvider {...methods}>
                    <form onSubmit={onSubmit} className="flex flex-col gap-4" data-test="supplier-odoo-onboarding">
                        <OdooFields />
                        <TotemPrimaryButton
                            type="submit"
                            disabled={loading}
                            data-test="supplier-save-odoo-association"
                            requiredPermissions={[TotadminPermission.SuppliersManage]}
                        >
                            {loading ? 'Enregistrement…' : 'Enregistrer l’association'}
                        </TotemPrimaryButton>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};
